<template>
  <div class="flex flex-col gap-3">
    <div>
      <SelectMenu
        :initial-value="selectedTemplateName"
        :options="templates.map(t => t.name)"
        @selected-option="setTemplate"
      />
      <router-link to="/settings/templates/email" target="_blank">
        <Button class="text-indigo-600 p-0" variant="link">Template Library</Button>
      </router-link>
    </div>

    <div>
      <Label class="text-base sm:text-xl mb-1 sm:mb-2">Subject</Label>
      <Input
        placeholder="Greetings from earth!"
        :model-value="subject"
        @update:model-value="value => emit('update:subject', `${value}`)"
      />
    </div>

    <div>
      <Label class="text-base sm:text-xl mb-1 sm:mb-2">Message</Label>
      <QuillInput
        ref="quillInputRef"
        name="email-body"
        label="Body"
        :label-sr-only="true"
        size="large"
        :model-value="internalBody"
        @update:model-value="handleBodyUpdate"
      />
    </div>

    <MergeTagSelect
      :label="null"
      :float-dropdown="true"
      @selected-tag="tag => quillInputRef?.insertMergeTagAtCursor(tag)"
    />

    <div v-if="showFileAttachment || deliveryMode" class="flex items-center justify-between">
      <FileInputBasic
        v-if="showFileAttachment"
        :multiple="true"
        :model-value="files"
        accept="*"
        @update:model-value="(value: FileList | null) => emit('update:files', value)"
      />
      <DeliveryModeEmailBadge v-if="deliveryMode" :delivery-mode="deliveryMode" />
    </div>
    <div v-if="isScheduled">
      <Label class="text-base sm:text-xl mb-1 sm:mb-2">Send On</Label>
      <DatePicker
        v-model="invokeOn as string"
        granularity="hour"
        disable-past
        @update:model-value="
          value => {
            emit('update:invoke-on', `${value}`);
          }
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import QuillInput from './QuillInput.vue';
import MergeTagSelect from './merge_tag/MergeTagSelect/index.vue';
import SelectMenu from './SelectMenu.vue';
import FileInputBasic from './FileInputBasic.vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { mergeTagQuillParser } from './merge_tag/quill_input/mergeTagQuillParser';
import { Api } from '@/api';
import type { DeliveryMode, EmailTemplateEntity } from '@/api/entity_types/EmailTemplate';
import { Label } from '../ui/label';
import DeliveryModeEmailBadge from './EmailBadge/DeliveryModeEmailBadge.vue';
import DatePicker from '../ui/date_picker/DatePicker.vue';

type Props = {
  subject?: string;
  body?: string;
  files?: FileList | null;
  invokeOn?: string;
  isScheduled?: boolean;
  showFileAttachment?: boolean;
  deliveryMode?: DeliveryMode;
};

const props = withDefaults(defineProps<Props>(), {
  subject: '',
  body: '',
  files: null,
  invokeOn: '',
  isScheduled: false,
  showFileAttachment: false,
  emailBadge: undefined,
});

const emit = defineEmits<{
  'update:subject': [value: string];
  'update:body': [value: string];
  'update:files': [value: FileList | null];
  'update:invoke-on': [value: string];
}>();

const quillInputRef = ref<{ insertMergeTagAtCursor: (tag: string) => void } | null>(null);

const templates = ref<EmailTemplateEntity[]>([]);
const selectedTemplateId = ref(0);
const internalBody = ref('');

const selectedTemplateName = computed(() => {
  const template = templates.value.find(t => t.id === selectedTemplateId.value);
  return template ? template.name : 'Choose a template';
});

const handleBodyUpdate = (value: string): void => {
  internalBody.value = value;
  emit('update:body', mergeTagQuillParser().serialize(value));
};

const fetchTemplates = async (): Promise<void> => {
  const data = await Api.EmailTemplates.list();

  templates.value = data.records;
};

const setTemplate = (templateName: string): void => {
  if (templates.value.length === 0) return;

  const template = templates.value.find(t => t.name === templateName);
  if (!template) return;

  nextTick(() => {
    emit('update:subject', template.subject);
    internalBody.value = mergeTagQuillParser().parse(template.body);
    emit('update:body', template.body);
    selectedTemplateId.value = template.id;
  });
};

onMounted(() => {
  fetchTemplates();
  if (props.body) {
    internalBody.value = mergeTagQuillParser().parse(props.body);
  }
});
</script>
